import { Form, Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createTransaction } from "@/features/banking/api";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import type { BankTransaction } from "@/features/banking/types";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  date: Yup.string().required("Date is required"),
  description: Yup.string().trim().required("Description is required"),
  // Signed decimal: optional leading -, up to 2 decimal places. Validated as a
  // string so we never coerce money through a float.
  amount: Yup.string()
    .required("Amount is required")
    .matches(/^-?\d+(\.\d{1,2})?$/, "Enter a signed amount, e.g. -42.50"),
  category: Yup.string(),
  reconciled: Yup.boolean(),
});

interface AddTransactionValues {
  date: string;
  description: string;
  amount: string;
  category: string;
  reconciled: boolean;
}

const initialValues: AddTransactionValues = {
  date: "",
  description: "",
  amount: "",
  category: "",
  reconciled: false,
};

interface AddTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  accountId: string;
  onCreated: (txn: BankTransaction) => void;
}

export function AddTransactionDialog({
  open,
  onOpenChange,
  clientId,
  accountId,
  onCreated,
}: AddTransactionDialogProps) {
  async function handleSubmit(values: AddTransactionValues) {
    try {
      const txn = await createTransaction(clientId, accountId, {
        date: values.date,
        description: values.description.trim(),
        amount: values.amount,
        category: values.category.trim() || undefined,
        reconciled: values.reconciled,
      });
      toast.success("Transaction added");
      onCreated(txn);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the transaction.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add transaction</DialogTitle>
          <DialogDescription>
            Use a negative amount for a debit (money out) and a positive amount for a credit.
          </DialogDescription>
        </DialogHeader>
        <Formik<AddTransactionValues>
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="date" label="Date" type="date" autoFocus />
              <FormField
                name="description"
                label="Description"
                placeholder="e.g. Office supplies"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="amount"
                  label="Amount"
                  placeholder="-42.50"
                  inputMode="decimal"
                />
                <FormField name="category" label="Category" placeholder="Optional" />
              </div>
              <CheckboxField name="reconciled" label="Reconciled" />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add transaction"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
